import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../store/hooks';
import { Pagination } from './Pagination';
import { TableHead } from './TableHead';
import { TableRow } from './TableRow';
import { useDataFreshness } from '../hooks/useDataFreshness';
import { useTableCellActions } from '../hooks/useTableCellActions';
import { updateURL } from '../features/table/table.actions';
import { convertUrlToEnv, getQueryParamValue } from '../misc/functions';
import { checkboxToggled } from '../features/table/table.slice';
import { selectPreparedData } from '../features/table/table.selectors';
import { COLUMNS } from '../misc/columns.constants';
import { WEBSITES_DATA_FILENAME } from '../misc/misc.constants';

export function Table() {
  const dispatch = useAppDispatch();
  const sort = useAppSelector((state) => state.table.sort);
  const filters = useAppSelector((state) => state.table.filters);
  const tags = useAppSelector((state) => state.table.tags);
  const preparedData = useAppSelector(selectPreparedData);
  const showColumns = useAppSelector((state) => state.table.showColumns);
  const websitesData = useAppSelector((state) => state.table.websitesData);
  const currentPage = useAppSelector((state) => state.table.currentPage);
  const perPage = useAppSelector((state) => state.table.perPage);
  const checkboxes = useAppSelector((state) => state.table.checkboxes);
  const indexOfLastItem = currentPage * perPage;
  const indexOfFirstItem = indexOfLastItem - perPage;
  const currentItems = preparedData.slice(indexOfFirstItem, indexOfLastItem);
  const env = websitesData?.env;
  const columns = websitesData?.columns ?? [];
  const convertLinksTo =
    getQueryParamValue('convertLinksTo') || getQueryParamValue('clt');
  const convertLinks = convertLinksTo && convertLinksTo !== env;

  const onTableBodyClick = useTableCellActions(columns);
  useDataFreshness();

  useEffect(() => {
    dispatch(updateURL());
  }, [filters, tags, sort, showColumns, perPage, currentPage, dispatch]);

  function onCheckboxChange(index: number) {
    dispatch(checkboxToggled(index));
  }

  if (!websitesData || websitesData.websites.length === 0) {
    return (
      <div className="full-height">
        <h4 data-qa="emptyDataFile">
          No data to show. Please check "{WEBSITES_DATA_FILENAME}" file.
        </h4>
      </div>
    );
  } else if (preparedData.length === 0) {
    return (
      <div className="full-height">
        <h4 data-qa="noResults">No data to show, please check your filters.</h4>
      </div>
    );
  } else if (showColumns.length === 0) {
    return (
      <div className="full-height">
        <h4 data-qa="noColumns">
          No columns to show, please check customize columns.
        </h4>
      </div>
    );
  } else {
    return (
      <>
        <Pagination position="above" />
        <section className="table">
          <table>
            <TableHead showColumns={showColumns} />

            <tbody onClick={onTableBodyClick}>
              {currentItems.map((websiteData, localIndex) => {
                const host = ((convertLinks &&
                  convertUrlToEnv(
                    websiteData[COLUMNS.website] as string,
                    convertLinksTo,
                  )) ||
                  websiteData[COLUMNS.host]) as string;
                const websiteIndex = indexOfFirstItem + localIndex + 1;

                return (
                  <TableRow
                    key={websiteData[COLUMNS.website] as string}
                    websiteData={websiteData}
                    showColumns={showColumns}
                    host={host}
                    websiteIndex={websiteIndex}
                    filters={filters}
                    checkboxes={checkboxes}
                    onCheckboxChange={onCheckboxChange}
                  />
                );
              })}
            </tbody>
          </table>
        </section>
        <Pagination position="below" />
      </>
    );
  }
}
